import dayjs from 'dayjs';
import type {
  RouteViewModel,
  RouteViewModelObligationRow,
  RouteViewModelStop,
} from '../../types/routeViewModel';
import {
  buildRouteStopPresentation,
  buildRouteStopPresentationContext,
  ROUTE_STOP_KIND_LABELS,
} from '../../features/routes/routeSummaryProjection';
import { stopFullAddress } from '../routes/routeDetails/RouteStructureBlock';
import {
  summarizeStopKindDates,
  type WorkspaceRouteStopSlice,
} from '../workspace/workspaceRouteRegistryProjection';
import { getStatusLabel } from '../../ui';
import { getExecutionStatusLabel } from '../../features/routes/execution/statusFacade';
import {
  getStopExecutionActualAt,
  getStopExecutionState,
} from '../../features/routes/routeViewExecutionHelpers';

const EXPENSE_STAGE_ORDER: Record<string, number> = {
  PREPAYMENT: 0,
  FINAL: 1,
};

export type RouteContextApplicationRow = {
  applicationId: number;
  number: string;
  clientName: string;
};

export type RouteContextProblemItem = {
  level: 'critical' | 'warning' | 'info';
  message: string;
};

function partyName(entity: unknown): string | null {
  if (!entity || typeof entity !== 'object') return null;
  const raw = (entity as Record<string, unknown>).name;
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

function driverName(driver: Record<string, unknown> | null | undefined): string | null {
  if (!driver) return null;
  const fullName = driver.fullName;
  if (typeof fullName === 'string' && fullName.trim()) return fullName.trim();
  return partyName(driver);
}

function vehicleLabel(vehicle: Record<string, unknown> | null | undefined): string | null {
  if (!vehicle) return null;
  const brand = vehicle.brand != null ? String(vehicle.brand).trim() : '';
  const num = vehicle.registrationNumber != null ? String(vehicle.registrationNumber).trim() : '';
  const name = vehicle.name != null ? String(vehicle.name).trim() : '';
  const parts = [name || brand, num].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}

export function missingAssigned(value: string | null | undefined): string {
  return value && value.trim() && value.trim() !== '—' ? value.trim() : 'Не назначен';
}

export function projectRouteApplications(
  applications: Record<string, unknown>[] | null | undefined
): RouteContextApplicationRow[] {
  if (!applications?.length) return [];
  return applications
    .map((link) => {
      const app = (link.application ?? null) as Record<string, unknown> | null;
      const client = (app?.client ?? null) as Record<string, unknown> | null;
      const applicationId = Number(link.applicationId ?? app?.id ?? 0);
      const number =
        (app?.internalNumber != null && String(app.internalNumber).trim()) ||
        (applicationId > 0 ? String(applicationId) : '—');
      return {
        applicationId,
        number,
        clientName: partyName(client) ?? '—',
      };
    })
    .filter((row) => row.applicationId > 0 || row.number !== '—');
}

export function projectRouteProblems(routeView: RouteViewModel): RouteContextProblemItem[] {
  const items: RouteContextProblemItem[] = [];
  const issues = routeView.execution?.controlIssues;
  if (issues) {
    for (const item of issues.critical ?? []) {
      items.push({ level: 'critical', message: item.message });
    }
    for (const item of issues.warnings ?? []) {
      items.push({ level: 'warning', message: item.message });
    }
  }
  for (const blocker of routeView.executionDashboard?.executionBlockers ?? []) {
    if (blocker?.trim()) items.push({ level: 'warning', message: blocker.trim() });
  }
  return items.slice(0, 6);
}

export function stopSlicesFromRouteView(stops: RouteViewModelStop[] | null | undefined): WorkspaceRouteStopSlice[] {
  return (stops ?? []).map((stop) => ({
    type: typeof stop.type === 'string' ? stop.type : null,
    position: typeof stop.position === 'number' ? stop.position : null,
    plannedAt: typeof stop.plannedAt === 'string' ? stop.plannedAt : null,
    actualAt: getStopExecutionActualAt(stop),
  }));
}

export function countStopsByKind(stops: RouteViewModelStop[] | null | undefined): {
  loading: number;
  unloading: number;
} {
  let loading = 0;
  let unloading = 0;
  for (const stop of stops ?? []) {
    const kind = String(stop.type || '').toLowerCase();
    if (kind === 'loading') loading += 1;
    if (kind === 'unloading') unloading += 1;
  }
  return { loading, unloading };
}

export function projectRouteExecutionDates(routeView: RouteViewModel) {
  const stops = stopSlicesFromRouteView(routeView.structure?.stops);
  const dates = routeView.operation.dates;
  return {
    loading: summarizeStopKindDates(stops, 'loading', {
      planned: dates.plannedLoading,
      actual: dates.actualLoading,
    }),
    unloading: summarizeStopKindDates(stops, 'unloading', {
      planned: dates.plannedUnloading,
      actual: dates.actualUnloading,
    }),
    stopCounts: countStopsByKind(routeView.structure?.stops),
  };
}

export function projectRouteTransport(routeView: RouteViewModel) {
  const parties = routeView.parties;
  return {
    carrier: missingAssigned(partyName(parties.carrier)),
    driver: missingAssigned(driverName(parties.driver as Record<string, unknown> | null)),
    vehicle: missingAssigned(vehicleLabel(parties.vehicle as Record<string, unknown> | null)),
    trailer: missingAssigned(vehicleLabel(parties.trailerVehicle as Record<string, unknown> | null)),
  };
}

export function projectRouteCargoSummary(routeView: RouteViewModel) {
  const summary = routeView.structure?.cargoSummary;
  return {
    lineCount: summary?.lineCount ?? 0,
    totalQuantityPlaces: summary?.totalQuantityPlaces ?? 0,
    totalWeightKg: summary?.totalWeightKg ?? 0,
    totalVolumeM3: summary?.totalVolumeM3 ?? 0,
  };
}

export function formatWeightKg(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} кг`;
}

export function formatVolumeM3(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '—';
  return `${value.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} м³`;
}

export type RouteContextItineraryStop = {
  position: number;
  kind: 'loading' | 'unloading' | 'other';
  kindLabel: string;
  applicationLabel: string | null;
  city: string;
  addressFull: string;
  plannedDate: string | null;
  actualDate: string | null;
  statusLabel: string;
};

function formatDrawerDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = dayjs(value);
  return d.isValid() ? d.format('DD.MM.YYYY') : null;
}

function stopStatusLabel(stop: RouteViewModelStop): string {
  const raw = getStopExecutionState(stop);
  if (!raw) return '—';
  const entityLabel = getStatusLabel(raw);
  if (entityLabel !== '—') return entityLabel;
  return getExecutionStatusLabel(raw);
}

export function sortExpenseObligations(rows: RouteViewModelObligationRow[]): RouteViewModelObligationRow[] {
  return [...rows].sort((a, b) => {
    const stageA = String(a.stage || '').toUpperCase();
    const stageB = String(b.stage || '').toUpperCase();
    const orderA = EXPENSE_STAGE_ORDER[stageA] ?? 99;
    const orderB = EXPENSE_STAGE_ORDER[stageB] ?? 99;
    if (orderA !== orderB) return orderA - orderB;
    return Number(a.id) - Number(b.id);
  });
}

export function projectRouteDrawerExpenseObligations(
  finance: RouteViewModel['finance']
): RouteViewModelObligationRow[] {
  const rows = (finance?.obligations ?? []).filter(
    (row) => String(row.direction || '').toUpperCase() === 'EXPENSE'
  );
  return sortExpenseObligations(rows);
}

export function projectRouteExecutionItinerary(routeView: RouteViewModel): RouteContextItineraryStop[] {
  const stops = routeView.structure?.stops ?? [];
  const applications = routeView.applications ?? [];
  const lw = routeView.operation.loadingWarehouse ?? null;
  const uw = routeView.operation.unloadingWarehouse ?? null;
  const ctx = buildRouteStopPresentationContext({
    applications,
    loadingWarehouse: lw,
    unloadingWarehouse: uw,
    routeLoadingCity: routeView.operation.direction?.loadingCity ?? null,
    routeUnloadingCity: routeView.operation.direction?.unloadingCity ?? null,
  });

  return [...stops]
    .map((stop, index) => {
      const presentation = buildRouteStopPresentation(stop, ctx);
      const kindRaw = String(stop.type || '').toLowerCase();
      const kind: RouteContextItineraryStop['kind'] =
        kindRaw === 'loading' ? 'loading' : kindRaw === 'unloading' ? 'unloading' : 'other';
      const kindLabel =
        kind === 'loading'
          ? ROUTE_STOP_KIND_LABELS.loading
          : kind === 'unloading'
            ? ROUTE_STOP_KIND_LABELS.unloading
            : 'Точка';
      const position = typeof stop.position === 'number' ? stop.position : index + 1;
      const fullAddress = stopFullAddress(stop, applications, lw, uw, kind === 'loading' ? 'loading' : 'unloading');
      const appNum = presentation.applicationNumber?.trim();
      const plannedRaw = typeof stop.plannedAt === 'string' ? stop.plannedAt : null;
      const actualRaw = getStopExecutionActualAt(stop);
      return {
        position,
        kind,
        kindLabel,
        applicationLabel: appNum ? `Заявка ${appNum}` : null,
        city: presentation.displayCity !== '—' ? presentation.displayCity : '—',
        addressFull: fullAddress.trim() || '—',
        plannedDate: formatDrawerDate(plannedRaw),
        actualDate: formatDrawerDate(actualRaw),
        statusLabel: stopStatusLabel(stop),
      };
    })
    .sort((a, b) => a.position - b.position);
}
